import { config } from "/app.config"
import { isFlagEnabled } from "@/common/helpers/featureFlags.helper"

export const accountFlagsLoaded = (state) => {
  return state.accountFlags !== null
}

/**
 * Whether a feature exists for the current portal: the account featureFlags response omits
 * flags unavailable to the portal, so presence means availability — no extra request needed.
 * Returns false until account flags are loaded — callers gate like other async menu/view state.
 */
export const isFeatureAvailable = (state) => (flagKey) => {
  return !!state.accountFlags && flagKey in state.accountFlags
}

/**
 * Effective value of a flag for the current account.
 * `fallback` applies until flags are loaded (or if the key is absent) — pass the registry default the caller expects.
 */
export const isFeatureEnabled = (state) => (flagKey, fallback = false) => {
  const flag = state.accountFlags?.[flagKey]

  if (flag) {
    return isFlagEnabled(flag.effective)
  }

  return fallback
}

// Portal admins bypass the account-level value but not portal-level availability,
// mirroring server-side enforcement — shared by every "is X allowed" gate below so the
// bypass rule lives in one place instead of being repeated in each consuming component.
const isAllowed = (getters, rootGetters, flagKey) => {
  return getters.isFeatureAvailable(flagKey) && (rootGetters["Profile/isPortalAdmin"] || getters.isFeatureEnabled(flagKey))
}

export const isPlayersAllowed = (state, getters, rootState, rootGetters) => {
  return isAllowed(getters, rootGetters, "webPlayersEnabled")
}

export const isHlsStreamingAllowed = (state, getters, rootState, rootGetters) => {
  return isAllowed(getters, rootGetters, "hlsStreamingEnabled")
}

export const isGeoBlockingAllowed = (state, getters, rootState, rootGetters) => {
  return isAllowed(getters, rootGetters, "geoBlockingEnabled")
}

export const isIpBlockingAllowed = (state, getters, rootState, rootGetters) => {
  return isAllowed(getters, rootGetters, "ipBlockingEnabled")
}

export const isCtlAllowed = (state, getters, rootState, rootGetters) => {
  return isAllowed(getters, rootGetters, "ctlLineEnabled")
}

// Whether the CTL tab and Call-in analytics exist at all. Never on a portal where ctlLineEnabled isn't registered.
// Zeno shows them whenever the flag is registered, so accounts without it enabled get the upgrade flow;
// white-label portals have no upgrade flow, so they need the flag effectively enabled.
export const isCtlVisible = (state, getters) => {
  return config.isZeno ? getters.isFeatureAvailable("ctlLineEnabled") : getters.isCtlAllowed
}

export const isZasAllowed =(state, getters, rootState, rootGetters) => {
  return isAllowed(getters, rootGetters, "zasEnabled")
}

export const isPodcastRecordingAllowed = (state, getters, rootState, rootGetters) => {
  return isAllowed(getters, rootGetters, "podcastRecordingEnabled")
}

export const isFinanceAllowed = (state, getters, rootState, rootGetters) => {
  return isAllowed(getters, rootGetters, "financeTabEnabled")
}

// The flag describes the account, not the user: campaigns also need the user to manage it (Acl/isAccountAdmin).
export const isAdvertisingCampaignsAllowed = (state, getters, rootState, rootGetters) => {
  return isAllowed(getters, rootGetters, "advertisingCampaignsEnabled")
}

export const isPodcastBotAllowed = (state, getters, rootState, rootGetters) => {
  return isAllowed(getters, rootGetters, "podcastBotEnabled")
}

// Whether the portal offers PodcastBot at all (the flag is registered), not whether this account has it.
export const isPodcastBotAvailable = (state, getters) => {
  return getters.isFeatureAvailable("podcastBotEnabled")
}

// With a checkout, the bot is sold through its own upsell flow (type chooser + AI route), shown to every account
// of a portal that offers it — accounts without it are the ones being upsold.
export const isPodcastBotUpsell = (state, getters) => {
  return config.hasSubscriptions && getters.isPodcastBotAvailable
}

// Without a checkout there is nothing to upsell, so the bot is a plain creation method alongside Create/Import,
// offered only to accounts that have it.
export const isPodcastBotCreationMethod = (state, getters) => {
  return !config.hasSubscriptions && getters.isPodcastBotAllowed
}

export const isSoundExchangeReportingAllowed =(state, getters, rootState, rootGetters) => {
  return isAllowed(getters, rootGetters, "soundExchangeReportingEnabled")
}

export const isRawDataExportAllowed = (state, getters, rootState, rootGetters) => {
  return isAllowed(getters, rootGetters, "rawDataExportEnabled")
}

export const isPortalNotificationsAllowed = (state, getters, rootState, rootGetters) => {
  return isAllowed(getters, rootGetters, "portalNotificationsEnabled")
}
